/**
 * Audio System - Text-to-Speech using Web Speech API
 * Provides letter sounds and word pronunciation for educational gameplay
 */

import { get } from 'svelte/store';
import { settings } from '../stores/settings';
import { soundManifest } from '../services/soundManifest';

/**
 * Audio configuration for different speech types
 */
interface SpeechConfig {
  rate: number;
  pitch: number;
  volume: number;
}

/**
 * Audio System Class
 * Singleton service for managing game audio via Web Speech API
 */
export class AudioSystem {
  private synthesis: SpeechSynthesis | null = null;
  private isAvailable: boolean = false;
  private currentUtterance: SpeechSynthesisUtterance | null = null;
  private audioQueue: Array<() => Promise<void>> = [];
  private isProcessingQueue: boolean = false;

  // HTML5 Audio for pre-recorded sounds
  private audioElements: Map<string, HTMLAudioElement> = new Map();
  private currentAudio: HTMLAudioElement | null = null;
  private maxCachedSounds: number = 50; // Limit cache size

  constructor() {
    // Check if Web Speech API is available
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      this.synthesis = window.speechSynthesis;
      this.isAvailable = true;
    } else {
      console.warn('Web Speech API not available - audio will be disabled');
      this.isAvailable = false;
    }
  }

  /**
   * Check if audio is available
   */
  public isAudioAvailable(): boolean {
    return this.isAvailable;
  }

  /**
   * Play letter sound (phonetic pronunciation)
   * @param letter - Single letter to pronounce
   * @returns Promise that resolves when speech completes
   */
  public async playLetterSound(letter: string): Promise<void> {
    if (!this.isAvailable || !this.synthesis) {
      return Promise.resolve();
    }

    const { soundEnabled, volume } = get(settings);
    if (!soundEnabled) {
      return Promise.resolve();
    }

    return this.queueSpeech(() => this.speak(letter, {
      rate: 0.7,   // Slower for clarity
      pitch: 1.2,  // Higher pitch for child-friendly voice
      volume
    }));
  }

  /**
   * Pronounce a complete word
   * @param word - Word to pronounce
   * @returns Promise that resolves when speech completes
   */
  public async pronounceWord(word: string): Promise<void> {
    if (!this.isAvailable || !this.synthesis) {
      return Promise.resolve();
    }

    const { soundEnabled, volume } = get(settings);
    if (!soundEnabled) {
      return Promise.resolve();
    }

    return this.queueSpeech(() => this.speak(word, {
      rate: 0.8,   // Normal speed for words
      pitch: 1.1,  // Slightly higher pitch
      volume
    }));
  }

  /**
   * Play object name sound
   * @param name - Object name to pronounce
   * @param immediate - If true, drop sound if audio is busy instead of queueing
   * @returns Promise that resolves when speech completes
   */
  public async playObjectSound(name: string, immediate: boolean = false): Promise<void> {
    if (!this.isAvailable || !this.synthesis) {
      return Promise.resolve();
    }

    const { soundEnabled, volume } = get(settings);
    if (!soundEnabled) {
      return Promise.resolve();
    }

    // Drop sound if audio is busy and immediate mode is enabled
    if (immediate && this.isAudioPlaying()) {
      return Promise.resolve();
    }

    return this.queueSpeech(() => this.speak(name, {
      rate: 0.8,   // Normal speed
      pitch: 1.1,  // Slightly higher pitch
      volume
    }));
  }

  /**
   * Cancel all current and queued speech
   */
  public cancelAll(): void {
    if (!this.synthesis) return;

    this.synthesis.cancel();
    this.audioQueue = [];
    this.isProcessingQueue = false;
    this.currentUtterance = null;
  }

  /**
   * Pause current speech
   */
  public pause(): void {
    if (!this.synthesis) return;
    this.synthesis.pause();
  }

  /**
   * Resume paused speech
   */
  public resume(): void {
    if (!this.synthesis) return;
    this.synthesis.resume();
  }

  /**
   * Set volume level
   * @param level - Volume level 0-1
   */
  public setVolume(level: number): void {
    // Volume is handled through settings store
    // Individual utterances will use the volume from settings
    const clampedVolume = Math.max(0, Math.min(1, level));
    settings.setVolume(clampedVolume);
  }

  /**
   * Core speech synthesis method
   * @param text - Text to speak
   * @param config - Speech configuration
   * @returns Promise that resolves when speech completes
   */
  private speak(text: string, config: SpeechConfig): Promise<void> {
    if (!this.synthesis) {
      return Promise.resolve();
    }

    // Cancel any current speech
    this.synthesis.cancel();

    return new Promise((resolve) => {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = config.rate;
      utterance.pitch = config.pitch;
      utterance.volume = config.volume;

      // Event handlers
      utterance.onend = () => {
        this.currentUtterance = null;
        resolve();
      };

      utterance.onerror = (event) => {
        console.error('Speech synthesis error:', event);
        this.currentUtterance = null;
        resolve(); // Graceful failure - don't block game
      };

      // Store current utterance
      this.currentUtterance = utterance;

      // Speak
      this.synthesis!.speak(utterance);
    });
  }

  /**
   * Queue speech to prevent overlapping
   * @param speechFn - Function that returns a speech promise
   * @returns Promise that resolves when speech completes
   */
  private async queueSpeech(speechFn: () => Promise<void>): Promise<void> {
    return new Promise((resolve) => {
      this.audioQueue.push(async () => {
        await speechFn();
        resolve();
      });

      if (!this.isProcessingQueue) {
        this.processQueue();
      }
    });
  }

  /**
   * Process the audio queue sequentially
   */
  private async processQueue(): Promise<void> {
    if (this.isProcessingQueue || this.audioQueue.length === 0) {
      return;
    }

    this.isProcessingQueue = true;

    while (this.audioQueue.length > 0) {
      const speechFn = this.audioQueue.shift();
      if (speechFn) {
        await speechFn();
      }
    }

    this.isProcessingQueue = false;
  }

  /**
   * Get supported voices (for future enhancement)
   */
  public getVoices(): SpeechSynthesisVoice[] {
    if (!this.synthesis) return [];
    return this.synthesis.getVoices();
  }

  /**
   * Test audio playback
   * Useful for debugging and user feedback
   */
  public async testAudio(): Promise<boolean> {
    if (!this.isAvailable) return false;

    try {
      await this.speak('Test', {
        rate: 1.0,
        pitch: 1.0,
        volume: get(settings).volume
      });
      return true;
    } catch (error) {
      console.error('Audio test failed:', error);
      return false;
    }
  }

  /**
   * Play a pre-recorded sound file (HTML5 Audio)
   * @param filePath - Path to audio file (e.g., "letters/A.mp3")
   * @returns Promise that resolves when sound completes
   */
  public async playSound(filePath: string): Promise<void> {
    const { soundEnabled, volume } = get(settings);
    if (!soundEnabled) {
      return Promise.resolve();
    }

    return this.queueSpeech(() => this.playSoundFile(filePath, volume));
  }

  /**
   * Check if audio is currently playing
   */
  public isAudioPlaying(): boolean {
    return this.isProcessingQueue || this.currentAudio !== null || this.currentUtterance !== null;
  }

  /**
   * Play letter name sound (e.g., "ay", "bee", "see")
   * @param letter - Single letter (case-insensitive)
   * @param immediate - If true, drop sound if audio is busy instead of queueing
   * @returns Promise that resolves when sound completes
   */
  public async playLetterName(letter: string, immediate: boolean = false): Promise<void> {
    // Drop sound if audio is busy and immediate mode is enabled
    if (immediate && this.isAudioPlaying()) {
      return Promise.resolve();
    }

    const filePath = soundManifest.getLetterNameSound(letter);
    if (!filePath) {
      console.warn(`[AudioSystem] No letter name sound for: ${letter}, falling back to TTS`);
      return this.playLetterSound(letter);
    }

    return this.playSound(filePath);
  }

  /**
   * Play phoneme sound (e.g., short "a", long "e")
   * @param letter - Single letter (case-insensitive)
   * @param type - Optional phoneme type ("short", "long", etc.)
   * @param immediate - If true, drop sound if audio is busy instead of queueing
   * @returns Promise that resolves when sound completes
   */
  public async playPhoneme(letter: string, type?: string, immediate: boolean = false): Promise<void> {
    // Drop sound if audio is busy and immediate mode is enabled
    if (immediate && this.isAudioPlaying()) {
      return Promise.resolve();
    }

    const filePath = soundManifest.getLetterPhonemeSound(letter, type);
    if (!filePath) {
      console.warn(`[AudioSystem] No phoneme sound for: ${letter} (${type}), falling back to TTS`);
      return this.playLetterSound(letter);
    }

    return this.playSound(filePath);
  }

  /**
   * Play combination/digraph sound (e.g., "ch", "sh", "th")
   * @param combination - Combination string (e.g., "ch", "sh", "th")
   * @param type - Optional type filter (e.g., "voiced", "unvoiced")
   * @param immediate - If true, drop sound if audio is busy instead of queueing
   * @returns Promise that resolves when sound completes
   */
  public async playCombinationSound(combination: string, type?: string, immediate: boolean = false): Promise<void> {
    // Drop sound if audio is busy and immediate mode is enabled
    if (immediate && this.isAudioPlaying()) {
      return Promise.resolve();
    }

    const filePath = soundManifest.getCombinationSound(combination, type);
    if (!filePath) {
      console.warn(`[AudioSystem] No combination sound for: ${combination} (${type}), falling back to TTS`);
      return this.playLetterSound(combination);
    }

    return this.playSound(filePath);
  }

  /**
   * Core HTML5 Audio playback method
   * @param filePath - Relative path from manifest
   * @param volume - Volume level 0-1
   * @returns Promise that resolves when sound completes
   */
  private playSoundFile(filePath: string, volume: number): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        // Get or create audio element
        let audio = this.audioElements.get(filePath);

        if (!audio) {
          audio = new Audio(soundManifest.getFullPath(filePath));
          audio.preload = 'auto';

          // Cache management - remove oldest if cache is full
          if (this.audioElements.size >= this.maxCachedSounds) {
            const firstKey = this.audioElements.keys().next().value;
            if (firstKey) {
              this.audioElements.delete(firstKey);
            }
          }

          this.audioElements.set(filePath, audio);
        }

        // Configure audio
        audio.volume = volume;
        audio.currentTime = 0; // Reset to start

        // Event handlers
        const onEnded = () => {
          audio!.removeEventListener('ended', onEnded);
          audio!.removeEventListener('error', onError);
          this.currentAudio = null;
          resolve();
        };

        const onError = (event: ErrorEvent) => {
          audio!.removeEventListener('ended', onEnded);
          audio!.removeEventListener('error', onError);
          console.error(`[AudioSystem] Failed to play sound: ${filePath}`, event);
          this.currentAudio = null;
          resolve(); // Graceful failure - don't block game
        };

        audio.addEventListener('ended', onEnded);
        audio.addEventListener('error', onError as any);

        // Play
        this.currentAudio = audio;
        audio.play().catch((error) => {
          console.error(`[AudioSystem] Play failed for ${filePath}:`, error);
          resolve(); // Graceful failure
        });
      } catch (error) {
        console.error(`[AudioSystem] Error creating audio for ${filePath}:`, error);
        resolve(); // Graceful failure
      }
    });
  }

  /**
   * Pre-load common sounds into cache
   * Call this during app initialization
   */
  public async preloadCommonSounds(): Promise<void> {
    if (!soundManifest.isManifestLoaded()) {
      console.warn('[AudioSystem] Cannot preload - manifest not loaded');
      return;
    }

    console.log('[AudioSystem] Pre-loading common sounds...');

    // Pre-load all letter name sounds (26 letters)
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
    const preloadPromises: Promise<void>[] = [];

    for (const letter of letters) {
      const filePath = soundManifest.getLetterNameSound(letter);
      if (filePath) {
        const promise = new Promise<void>((resolve) => {
          try {
            const audio = new Audio(soundManifest.getFullPath(filePath));
            audio.preload = 'auto';
            audio.addEventListener('canplaythrough', () => resolve(), { once: true });
            audio.addEventListener('error', () => resolve(), { once: true }); // Graceful failure
            this.audioElements.set(filePath, audio);
          } catch {
            resolve(); // Graceful failure
          }
        });
        preloadPromises.push(promise);
      }
    }

    // Wait for all to load (or fail gracefully)
    await Promise.all(preloadPromises);
    console.log(`[AudioSystem] Pre-loaded ${this.audioElements.size} sounds`);
  }
}

// Singleton instance for global use
export const audioSystem = new AudioSystem();
